/**
 * The Admin's live map, period dashboard, route analysis and route-anomaly console (W9-OPS-LIVE-MAP, D471, ADR-0089;
 * DevMobility C-050, I-093, C-006, C-031, A-006; X-ray A-09, A-10, A-29) — framework-neutral view-models.
 *
 *  - **Operações › Mapa ao vivo** (`live_map`): the market's drivers on the map with their presence (available, in ride,
 *    no signal, offline), the rides waiting for a driver, filters, the counts, the driver detail, and — for an operator
 *    who holds the dispatch words — the candidates of a waiting ride from ITS pickup (never a typed coordinate).
 *  - **Visão geral** (`dashboard`): the period (today / 7 / 30 days / custom, in the market's zone), supply now, the
 *    no-driver rate, revenue per currency, per local day, auto-refresh.
 *  - **Corrida › Analisar rota**: the three lines of a ride.
 *  - **Operações › Anomalias de rota** (`route_anomalies`): the alerts the worker raised, acknowledged and closed with a
 *    code of the closed vocabulary.
 *
 * The console only presents: the server authorizes every read at the market asked for. No colour literal: every map
 * colour is a token variable (map pins may be semantic — IMPORT-0001 §4). Neutral: no country, zone or currency here.
 */
import {
  type FleetMarkerView,
  type FleetPresenceView,
  type FleetView,
  type MapTilesView,
  type PeriodDashboardView,
  type RouteAnomalyAlertView,
  type RouteAnomalyResolutionCodeView,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { marketDate, marketDayStart } from './market-time.js';

export type LiveMapTone = 'neutral' | 'success' | 'warning' | 'danger';

// ---- authority (display only — the server re-authorizes every call) ------------------------------------------------

export function liveMapAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayDispatch: boolean; readonly mayAnalyze: boolean } {
  if (grants === undefined) return { mayView: false, mayDispatch: false, mayAnalyze: false };
  return {
    mayView: grantsAllow(grants, { permission: 'ops:view', target: 'GEO' }),
    // Candidates and the manual assignment are platform routes (`dispatch:view` / `dispatch:override`).
    mayDispatch: grantsAllow(grants, { permission: 'dispatch:override', target: 'PLATFORM', alsoRequires: ['dispatch:view'] }),
    mayAnalyze: grantsAllow(grants, { permission: 'ride:view', target: 'GEO' }),
  };
}

export function routeAnomalyAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'safety:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'safety:manage', target: 'GEO' }),
  };
}

// ---- the fleet ---------------------------------------------------------------------------------------------------

export const PRESENCES: readonly FleetPresenceView[] = ['AVAILABLE', 'IN_RIDE', 'NO_SIGNAL', 'OFFLINE'];

export function presenceTone(p: FleetPresenceView): LiveMapTone {
  return p === 'AVAILABLE' ? 'success' : p === 'NO_SIGNAL' ? 'warning' : 'neutral';
}

/** The pin colour of a presence — a token variable, never a literal. */
export function presenceColorVar(p: FleetPresenceView): string {
  switch (p) {
    case 'AVAILABLE':
      return 'var(--color-success-text)';
    case 'IN_RIDE':
      return 'var(--color-action-primary)';
    case 'NO_SIGNAL':
      return 'var(--color-warning)';
    case 'OFFLINE':
      return 'var(--color-text-tertiary)';
  }
}

/** A ride waiting for a driver is a danger pin (IMPORT-0001 §4: red is semantic and map-pin only). */
export const OPEN_RIDE_COLOR_VAR = 'var(--color-danger)';

/** How long ago a sign of life was, in the largest whole unit (for "há 3 min"). */
export function ageParts(seconds: number | undefined): { readonly unit: 'seconds' | 'minutes' | 'hours' | 'days'; readonly value: number } | undefined {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return undefined;
  if (seconds < 60) return { unit: 'seconds', value: Math.floor(seconds) };
  if (seconds < 3_600) return { unit: 'minutes', value: Math.floor(seconds / 60) };
  if (seconds < 86_400) return { unit: 'hours', value: Math.floor(seconds / 3_600) };
  return { unit: 'days', value: Math.floor(seconds / 86_400) };
}

export interface MapPoint {
  readonly id: string;
  readonly kind: 'driver' | 'ride';
  readonly latitude: number;
  readonly longitude: number;
  readonly colorVar: string;
  /** A last known (not live) position is drawn hollow. */
  readonly stale: boolean;
}

/** The points the map draws: every driver with a drawable position, then every waiting ride with a pickup. */
export function fleetMapPoints(view: FleetView | undefined): readonly MapPoint[] {
  if (view === undefined) return [];
  const out: MapPoint[] = [];
  for (const d of view.drivers) {
    if (d.position === undefined) continue;
    out.push({ id: d.driverId, kind: 'driver', latitude: d.position.latitude, longitude: d.position.longitude, colorVar: presenceColorVar(d.presence), stale: d.positionSource !== 'LIVE' });
  }
  for (const r of view.openRides) {
    if (r.pickup === undefined) continue;
    out.push({ id: r.rideId, kind: 'ride', latitude: r.pickup.latitude, longitude: r.pickup.longitude, colorVar: OPEN_RIDE_COLOR_VAR, stale: false });
  }
  return out;
}

/** The vehicle categories offered as a filter (the drivers' own, sorted). */
export function fleetCategories(view: FleetView | undefined): readonly string[] {
  const set = new Set<string>();
  for (const d of view?.drivers ?? []) for (const c of d.categories) set.add(c);
  return [...set].sort();
}

export function driverById(view: FleetView | undefined, id: string | undefined): FleetMarkerView | undefined {
  return id === undefined ? undefined : view?.drivers.find((d) => d.driverId === id);
}

/**
 * D797: the raster override of Integrações › Tiles do mapa, only while it is switched on and valid. Otherwise (absent, off,
 * invalid) the map draws the platform's own vector basemap, so a map is never missing for want of a tile source.
 */
export function tileOverride(tiles: MapTilesView | undefined): Extract<MapTilesView, { readonly status: 'CONFIGURED' }> | undefined {
  return tiles?.status === 'CONFIGURED' ? tiles : undefined;
}

// ---- the period ----------------------------------------------------------------------------------------------------

/**
 * The quick windows beside the always-visible from/to dates (owner, 2026-10-12): a preset FILLS the dates, editing a date
 * leaves every preset unpressed — there is no "custom" option to press before the dates appear.
 */
export type PeriodPreset = 'today' | '7d' | '30d';
export const PERIOD_PRESETS: readonly PeriodPreset[] = ['today', '7d', '30d'];

const nextDay = (day: string): string => new Date(Date.parse(`${day}T00:00:00.000Z`) + 86_400_000).toISOString().substring(0, 10);
const minusDays = (day: string, n: number): string => new Date(Date.parse(`${day}T00:00:00.000Z`) - n * 86_400_000).toISOString().substring(0, 10);

/**
 * A preset window in the market's zone: from the local midnight that starts it to the local midnight after today (so the
 * whole of today counts). Undefined when the zone cannot place a midnight.
 */
export function presetPeriod(preset: PeriodPreset, now: Date | string, timeZone: string | undefined): { readonly from: string; readonly to: string } | undefined {
  const days = presetDays(preset, now, timeZone);
  return customPeriod(days.fromDay, days.toDay, timeZone);
}

/** The local days a preset covers (`YYYY-MM-DD`, both inclusive) — what the from/to date fields show for it. */
export function presetDays(preset: PeriodPreset, now: Date | string, timeZone: string | undefined): { readonly fromDay: string; readonly toDay: string } {
  const today = marketDate(now, timeZone);
  return { fromDay: preset === 'today' ? today : minusDays(today, preset === '7d' ? 6 : 29), toDay: today };
}

/** A custom window of whole local days, `fromDay` … `toDay` inclusive. Undefined when malformed or reversed. */
export function customPeriod(fromDay: string, toDay: string, timeZone: string | undefined): { readonly from: string; readonly to: string } | undefined {
  const from = marketDayStart(fromDay, timeZone);
  const to = /^\d{4}-\d{2}-\d{2}$/.test(toDay.trim()) ? marketDayStart(nextDay(toDay.trim()), timeZone) : undefined;
  if (from === undefined || to === undefined || Date.parse(from) >= Date.parse(to)) return undefined;
  return { from, to };
}

export type PeriodCard = { readonly id: string } & (
  | { readonly kind: 'count'; readonly value: number }
  | { readonly kind: 'ratio'; readonly value: number }
  | { readonly kind: 'unavailable' }
);

/** The period's number cards — a rate over zero rides is `unavailable`, never 0 %. */
export function periodCards(view: PeriodDashboardView | undefined): readonly PeriodCard[] {
  if (view === undefined) return [];
  const r = view.rides;
  const rate = (id: string, v: number | undefined): PeriodCard => (v === undefined || !Number.isFinite(v) ? { id, kind: 'unavailable' } : { id, kind: 'ratio', value: v });
  const cards: PeriodCard[] = [
    { id: 'requested', kind: 'count', value: r.requested },
    rate('noDriverRate', view.rates?.noDriverRate),
    { id: 'completed', kind: 'count', value: r.completed },
    rate('completionRate', view.rates?.completionRate),
    { id: 'cancelled', kind: 'count', value: r.cancelledBeforeAssignment + r.cancelledAfterAssignment },
  ];
  if (view.supplyNow !== undefined) {
    cards.push({ id: 'supplyOnline', kind: 'count', value: view.supplyNow.AVAILABLE + view.supplyNow.IN_RIDE });
    cards.push({ id: 'supplyNoSignal', kind: 'count', value: view.supplyNow.NO_SIGNAL });
  }
  return cards;
}

export interface RevenueRowView {
  readonly currency: string;
  readonly rides: number;
  readonly dueMinor: bigint;
  readonly receivedMinor: bigint;
  readonly platformMinor: bigint;
  readonly driversMinor: bigint;
}

const minor = (raw: string): bigint | undefined => (/^-?\d+$/.test(raw) ? BigInt(raw) : undefined);

/** Revenue rows with exact bigint minor units; a row with an unknown currency or a malformed amount is left out. */
export function revenueRows(view: PeriodDashboardView | undefined): readonly RevenueRowView[] {
  const out: RevenueRowView[] = [];
  for (const r of view?.revenue ?? []) {
    const due = minor(r.dueMinor);
    const received = minor(r.receivedMinor);
    const platform = minor(r.platformMinor);
    const drivers = minor(r.driversMinor);
    if (!isIso4217Currency(r.currency) || due === undefined || received === undefined || platform === undefined || drivers === undefined) continue;
    out.push({ currency: r.currency, rides: r.rides, dueMinor: due, receivedMinor: received, platformMinor: platform, driversMinor: drivers });
  }
  return out;
}

/** The daily bars, each as a share of the busiest day (0–1). */
export function dayBars(view: PeriodDashboardView | undefined): readonly { readonly day: string; readonly requested: number; readonly completed: number; readonly noDriver: number; readonly share: number }[] {
  const days = view?.days ?? [];
  const max = Math.max(1, ...days.map((d) => d.requested));
  return days.map((d) => ({ ...d, share: d.requested / max }));
}

/** One local day of the trend chart: the rides that ended well, with no driver, and every other state (cancelled, still open). */
export interface DayTrendPoint {
  readonly day: string;
  readonly requested: number;
  readonly completed: number;
  readonly noDriver: number;
  /** requested − completed − noDriver: cancelled or still in flight. Never negative (completed and no-driver are disjoint). */
  readonly other: number;
}

const MAX_TREND_DAYS = 120;

/**
 * D513 (C-031): the per-day series for the column chart, one entry for EVERY local day of the window — a day the server
 * left out had no ride and is a real zero, not a hole. The window runs from the local day of `period.from` to the local day
 * of the last instant before `period.to`, widened to the days the server returned (a multi-market scope buckets each market
 * in its own zone). Empty when the server returned no day at all (no ride in the period): the chart then says so.
 */
export function dayTrend(view: PeriodDashboardView | undefined, timeZone: string | undefined): readonly DayTrendPoint[] {
  const days = view?.days ?? [];
  if (view === undefined || days.length === 0) return [];
  const byDay = new Map(days.map((d) => [d.day, d] as const));
  const sorted = [...byDay.keys()].sort();
  const fromMs = Date.parse(view.period.from);
  const toMs = Date.parse(view.period.to);
  let first = sorted[0] as string;
  let last = sorted[sorted.length - 1] as string;
  if (Number.isFinite(fromMs)) {
    const d = marketDate(new Date(fromMs), timeZone);
    if (d < first) first = d;
  }
  if (Number.isFinite(toMs)) {
    const d = marketDate(new Date(toMs - 1), timeZone);
    if (d > last) last = d;
  }
  const out: DayTrendPoint[] = [];
  for (let cursor = first; cursor <= last && out.length < MAX_TREND_DAYS; cursor = nextDay(cursor)) {
    const d = byDay.get(cursor);
    const requested = d?.requested ?? 0;
    const completed = d?.completed ?? 0;
    const noDriver = d?.noDriver ?? 0;
    out.push({ day: cursor, requested, completed, noDriver, other: Math.max(0, requested - completed - noDriver) });
  }
  return out;
}

export type RideStatusSliceId = 'completed' | 'cancelled' | 'noDriver' | 'open';

/**
 * D513: the period's rides by what became of them — completed, cancelled (before + after a driver was assigned), no driver,
 * and the rest (still in flight or in another state). `open` is the remainder and never negative; the server's no-driver
 * count is "never got a driver", which can also be a ride cancelled in the search, so the parts are not forced to add up to
 * `requested` (the remainder simply floors at zero rather than inventing a negative).
 */
export function statusSlices(view: PeriodDashboardView | undefined): readonly { readonly id: RideStatusSliceId; readonly value: number }[] {
  if (view === undefined) return [];
  const r = view.rides;
  const cancelled = r.cancelledBeforeAssignment + r.cancelledAfterAssignment;
  return [
    { id: 'completed', value: r.completed },
    { id: 'cancelled', value: cancelled },
    { id: 'noDriver', value: r.noDriver },
    { id: 'open', value: Math.max(0, r.requested - r.completed - cancelled - r.noDriver) },
  ];
}

// ---- route anomalies -----------------------------------------------------------------------------------------------

export function anomalyTone(severity: RouteAnomalyAlertView['severity']): LiveMapTone {
  return severity === 'high' ? 'danger' : severity === 'medium' ? 'warning' : 'neutral';
}

export const RESOLVE_CODES: readonly RouteAnomalyResolutionCodeView[] = ['RESOLVED_DRIVER_CONTACTED', 'RESOLVED_RIDER_CONFIRMED_SAFE', 'RESOLVED_ESCALATED_TO_SAFETY'];
export const DISMISS_CODES: readonly RouteAnomalyResolutionCodeView[] = ['DISMISSED_KNOWN_DETOUR', 'DISMISSED_GPS_NOISE', 'DISMISSED_FALSE_POSITIVE'];

/**
 * The actions an alert offers (none once closed): an open alert is acknowledged, resolved or dismissed; an acknowledged one can be
 * taken back (`reopen`: ACKNOWLEDGED → OPEN, the console's undo), resolved or dismissed.
 */
export function anomalyActions(alert: RouteAnomalyAlertView, mayManage: boolean): readonly ('acknowledge' | 'reopen' | 'resolve' | 'dismiss')[] {
  if (!mayManage || alert.status === 'RESOLVED' || alert.status === 'DISMISSED') return [];
  return alert.status === 'OPEN' ? ['acknowledge', 'resolve', 'dismiss'] : ['reopen', 'resolve', 'dismiss'];
}

/** The scope a policy version was set at, read from the version label (`safety.routeAnomaly@market:<id>#v3`, `…@region:BR-MG#v1` for a state, ADR-0124): the console says it in words. */
export type AnomalyPolicyOrigin =
  | { readonly kind: 'default' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'country' | 'region' | 'market'; readonly ref: string; readonly version: number };

export function anomalyPolicyOrigin(policyVersion: string): AnomalyPolicyOrigin | undefined {
  const at = policyVersion.slice(policyVersion.indexOf('@') + 1);
  if (!policyVersion.includes('@')) return undefined;
  if (at === 'default') return { kind: 'default' };
  if (at === 'invalid') return { kind: 'invalid' };
  const m = /^(country|region|market):(.+)#v(\d+)$/.exec(at);
  return m === null ? undefined : { kind: m[1] as 'country' | 'region' | 'market', ref: m[2] as string, version: Number(m[3]) };
}

/**
 * Why an alert was raised, as numbers: the value measured against the threshold of the policy in force (when the alert kept
 * it). Route deviation compares the projected distance to the expected one (in basis points of it); a stop compares the seconds
 * stood still. `night` is whether the trigger is the lone-at-night one (a shorter stop is enough).
 */
export interface AnomalyEvidence {
  readonly kind: 'deviation' | 'stop';
  readonly night: boolean;
  /** Measured: basis points of the expected distance, or seconds stopped. */
  readonly measured: number;
  /** The policy threshold in the same unit; undefined when the alert predates the snapshot. */
  readonly threshold: number | undefined;
  readonly expectedMeters: number;
  readonly projectedMeters: number;
}

export function anomalyEvidence(a: RouteAnomalyAlertView): AnomalyEvidence {
  const base = { expectedMeters: a.expectedMeters, projectedMeters: a.projectedMeters };
  if (a.trigger === 'route_anomaly') {
    return { kind: 'deviation', night: false, measured: a.deviationBasisPoints, threshold: a.thresholds?.routeDeviationBasisPoints, ...base };
  }
  const night = a.trigger === 'night_isolated_stop';
  return { kind: 'stop', night, measured: a.stoppedSeconds, threshold: night ? a.thresholds?.nightStopSeconds : a.thresholds?.maxStopSeconds, ...base };
}

